/* ============================================================
   Acies Motion — GSAP / ScrollTrigger / Lenis engine
   Halftone Lab · premium scroll & reveal layer
   ------------------------------------------------------------
   - Targets the existing acies-* section classes (no markup edits)
   - Word-mask headline reveals, scroll reveals, parallax, magnetic
     buttons, and optional Lenis smooth scroll (desktop only)
   - Fails open: any error or missing lib reveals all content
   - Respects prefers-reduced-motion and re-inits in the theme editor
   ============================================================ */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var cfg = window.AciesMotionConfig || {};
  var mq = function (q) { return window.matchMedia && window.matchMedia(q).matches; };
  var reduce = mq('(prefers-reduced-motion: reduce)');

  function showAll() { docEl.classList.remove('acies-anim'); }

  // Guard clauses: bail (and reveal everything) if we should not animate.
  if (!cfg.enabled || reduce) { showAll(); return; }
  if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') { showAll(); return; }

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var finePointer = mq('(hover: hover) and (pointer: fine)');
  var isMobile = mq('(max-width: 749px)');
  var EASE = 'power3.out';
  var WORD_EASE = 'power4.out';

  try {
    gsap.registerPlugin(ScrollTrigger);
    docEl.classList.add('acies-motion-init'); // cancels the <head> safety timer
  } catch (err) { showAll(); return; }

  /* ---------------- helpers ---------------- */
  function qa(root, sel) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function first(root, sel) { return (root || document).querySelector(sel); }

  // per-element once-guard so editor re-inits never double-process
  function claim(el, key) {
    key = key || 'aciesDone';
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  }

  // Split a headline into masked words, preserving <br> and other tags.
  function splitWords(el) {
    if (el.dataset.aciesSplit) return qa(el, '.acies-w__i');
    el.dataset.aciesSplit = '1';
    el.classList.add('acies-split');
    var frag = document.createDocumentFragment();
    Array.prototype.slice.call(el.childNodes).forEach(function (node) {
      if (node.nodeType === 3) { // text node -> word masks
        node.textContent.split(/(\s+)/).forEach(function (part) {
          if (part === '') return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          var outer = document.createElement('span');
          outer.className = 'acies-w';
          var inner = document.createElement('span');
          inner.className = 'acies-w__i';
          inner.textContent = part;
          outer.appendChild(inner);
          frag.appendChild(outer);
        });
      } else {
        frag.appendChild(node.cloneNode(true)); // keep <br>, spans, etc.
      }
    });
    el.innerHTML = '';
    el.appendChild(frag);
    return qa(el, '.acies-w__i');
  }

  // timeline factory: scroll-triggered or immediate (with delay)
  function seq(section, scroll, delay) {
    var vars = { defaults: { ease: EASE } };
    if (scroll) {
      vars.scrollTrigger = { trigger: section, start: 'top 82%', once: true };
    } else {
      vars.delay = delay || 0;
    }
    return gsap.timeline(vars);
  }

  function addWords(tl, head, pos) {
    if (!head) return;
    var words = splitWords(head);
    gsap.set(head, { opacity: 1 });
    tl.fromTo(words, { yPercent: 120 },
      { yPercent: 0, duration: 0.9, ease: WORD_EASE, stagger: 0.05 }, pos);
  }

  function addUp(tl, el, pos, y, dur) {
    if (!el) return;
    tl.fromTo(el, { opacity: 0, y: (y == null ? 24 : y) },
      { opacity: 1, y: 0, duration: (dur || 0.7) }, pos);
  }

  // staggered reveal for card/photo/item grids
  function revealStagger(nodes, y, stagger) {
    if (!nodes.length) return;
    nodes.forEach(function (el) { gsap.set(el, { y: (y == null ? 30 : y) }); });
    ScrollTrigger.batch(nodes, {
      start: 'top 90%',
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, { opacity: 1, y: 0, duration: 0.8, ease: EASE, stagger: (stagger || 0.08), overwrite: true });
      }
    });
  }

  // scroll-linked parallax on a full-bleed background image
  function parallax(section, img) {
    if (!img || isMobile) return; // skip on mobile for scroll perf
    gsap.set(img, { scale: 1.16, transformOrigin: '50% 50%' });
    gsap.fromTo(img, { yPercent: -7 }, {
      yPercent: 7,
      ease: 'none',
      scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true }
    });
  }

  /* ---------------- per-section wiring ---------------- */
  function initHeroes() {
    qa(document, '.acies-hero').forEach(function (s) {
      if (!claim(s)) return;
      parallax(s, first(s, '.acies-hero__img'));
      var tl = seq(s, false, 0.12);
      addUp(tl, first(s, '.acies-hero__sub'), 0, 16, 0.6);
      addWords(tl, first(s, '.acies-hero__head'), 0.12);
      addUp(tl, first(s, '.acies-hero__cta'), 0.55, 14, 0.6);
    });
  }

  function initCollectionBanners() {
    qa(document, '.acies-cbanner').forEach(function (s) {
      if (!claim(s)) return;
      parallax(s, first(s, '.acies-cbanner__img'));
      var tl = seq(s, false, 0.12);
      addUp(tl, first(s, '.acies-cbanner__eyebrow'), 0, 14, 0.6);
      addWords(tl, first(s, '.acies-cbanner__title'), 0.12);
      addUp(tl, first(s, '.acies-cbanner__desc'), 0.5, 16, 0.6);
    });
  }

  function initStatements() {
    qa(document, '.acies-stmt').forEach(function (s) {
      if (!claim(s)) return;
      parallax(s, first(s, '.acies-stmt__img'));
      var tl = seq(s, true, 0);
      addUp(tl, first(s, '.acies-stmt__sub'), 0, 18, 0.6);
      addWords(tl, first(s, '.acies-stmt__head'), 0.08);
      addUp(tl, first(s, '.acies-stmt__body'), 0.28, 20, 0.65);
      addUp(tl, first(s, '.acies-stmt__btn'), 0.4, 16, 0.55);
    });
  }

  function initProductGrids() {
    qa(document, '.acies-grid').forEach(function (s) {
      if (!claim(s)) return;
      // Header animates; the product cards render immediately (no hide→reveal).
      // Revealing content-heavy cards paints them on the reveal frame, which
      // stutters when several enter at once (esp. PDP recommendations). Cards
      // appearing instantly keeps grids buttery — the motion lives in the
      // hero, parallax, marquee and page transitions.
      var tl = seq(s, true, 0);
      addUp(tl, first(s, '.acies-grid__title'), 0, 18, 0.6);
      addUp(tl, first(s, '.acies-grid__viewall'), 0.08, 12, 0.5);
    });
  }

  function initPromos() {
    qa(document, '.acies-promo').forEach(function (s) {
      if (!claim(s)) return;
      parallax(s, first(s, '.acies-promo__bg'));
      var tl = seq(s, true, 0);
      addUp(tl, first(s, '.acies-promo__eyebrow'), 0, 16, 0.6);
      addWords(tl, first(s, '.acies-promo__head'), 0.1);
      var tiers = qa(s, '.acies-promo__tier');
      if (tiers.length) {
        tl.fromTo(tiers, { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: 0.6, stagger: 0.1 }, 0.3);
      }
      addUp(tl, first(s, '.acies-promo__foot'), 0.55, 12, 0.5);
      addUp(tl, first(s, '.acies-promo__btn'), 0.62, 12, 0.5);
    });
  }

  function initTrustBars() {
    qa(document, '.acies-trust').forEach(function (s) {
      if (!claim(s)) return;
      revealStagger(qa(s, '.acies-trust__item'), 14, 0.08);
    });
  }

  function initGalleries() {
    qa(document, '.acies-ig').forEach(function (s) {
      if (!claim(s)) return;
      // header animates; photo tiles render immediately (same rationale as
      // the product grid — many image tiles revealing at once paints-stutters)
      var tl = seq(s, true, 0);
      addUp(tl, first(s, '.acies-ig__title'), 0, 16, 0.6);
      addUp(tl, first(s, '.acies-ig__sub'), 0.08, 12, 0.5);
    });
  }

  // subtle magnetic pull on primary buttons (fine pointers only)
  function initMagnetic() {
    if (!finePointer) return;
    qa(document, '.acies-btn, .acies-stmt__btn, .acies-promo__btn').forEach(function (btn) {
      if (!claim(btn, 'aciesMag')) return;
      var s = 0.26;
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        gsap.to(btn, {
          x: (e.clientX - r.left - r.width / 2) * s,
          y: (e.clientY - r.top - r.height / 2) * s * 1.3,
          duration: 0.5, ease: 'power3.out'
        });
      });
      btn.addEventListener('mouseleave', function () {
        gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      });
    });
  }

  // Lenis smooth scroll — desktop pointers only, off in the editor
  function initLenis() {
    if (!cfg.smooth || cfg.designMode || isMobile || !finePointer) return;
    if (typeof window.Lenis === 'undefined' || window.__aciesLenis) return;
    try {
      var lenis = new window.Lenis({ lerp: 0.09, wheelMultiplier: 1, smoothWheel: true });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
      gsap.ticker.lagSmoothing(0);
      window.__aciesLenis = lenis;
    } catch (err) { /* native scroll is a fine fallback */ }
  }

  function initAll() {
    initHeroes();
    initCollectionBanners();
    initStatements();
    initProductGrids();
    initPromos();
    initTrustBars();
    initGalleries();
    initMagnetic();
  }

  /* ---------------- boot ---------------- */
  try {
    initLenis();
    initAll();
  } catch (err) {
    showAll();
    return;
  }

  // NOTE: we intentionally do NOT call ScrollTrigger.refresh() ourselves.
  // ScrollTrigger already auto-refreshes on 'load' and 'resize' (its default
  // autoRefreshEvents), which fires early — before the visitor scrolls down.
  // A manual refresh() forces a full synchronous reflow (~30-50ms); firing it
  // again on fonts.ready risked landing mid-scroll and showing as one janky
  // frame. If a late web-font shifts a trigger by a few px, that's invisible.
  // When we DO mutate the DOM ourselves (editor section reload) we still
  // refresh — see the shopify:section:load handler below.

  // theme editor: re-wire freshly rendered sections
  document.addEventListener('shopify:section:load', function () {
    try { initAll(); ScrollTrigger.refresh(); } catch (err) { showAll(); }
  });
})();
